import { useCallback, useEffect, useState } from "react";
import { MdReceipt } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import AjudaTela from "../../components/AjudaTela";
import { LuCopyPlus } from "react-icons/lu";
import Layout from "../../components/Layout";
import ModalCadastroAnimal from "../../components/modais/ModalCadastroAnimal";
import ModalCadastroCliente from "../../components/modais/ModalCadastroCliente";
import ModalCadastroFazenda from "../../components/modais/ModalCadastroFazenda";
import ModalCadastroFornecedor from "../../components/modais/ModalCadastroFornecedor";
import ModalCadastroPasto from "../../components/modais/ModalCadastroPasto";
import { api } from "../../services/api";
import { repositorioFazenda } from "../../sync/repositories/fazendas";
import {
  repositorioCliente,
  repositorioFornecedor,
} from "../../sync/repositories/financeiro";
import { nomePessoa } from "../../utils/nomesPessoa";
import {
  repositorioAnimal,
  repositorioMovimentacaoPecuaria,
  repositorioPasto,
} from "../../sync/repositories/pecuaria";

const AJUDA = {
  titulo: "Movimentações de Animais",
  descricao:
    "Registre aqui a entrada, saída ou transferência de animais com documento fiscal ou de transporte associado: Compra, Venda, Transferência entre fazendas ou Transferência entre pastos. É diferente da tela de Rotação de Pastos, que só move lotes entre pastos da mesma fazenda sem gerar documento.",
  secoes: [
    {
      titulo: "Dados da Movimentação",
      campos: [
        {
          nome: "Fazenda",
          obrigatorio: true,
          explicacao: "Fazenda de origem dessa movimentação.",
        },
        {
          nome: "Tipo de Movimentação",
          obrigatorio: true,
          explicacao: "Compra, Venda, Transferência entre fazendas ou Transferência entre pastos — define quais campos extra aparecem a seguir (Fornecedor, Cliente, Fazenda Destino ou Pasto Destino).",
        },
        {
          nome: "Número do Documento",
          obrigatorio: true,
          explicacao: "Número da nota fiscal, CT-e ou documento que acompanha essa movimentação.",
        },
        {
          nome: "Data de Emissão",
          obrigatorio: true,
          explicacao: "Data de emissão do documento.",
        },
        {
          nome: "Data de Vencimento",
          obrigatorio: false,
          explicacao: "Prazo de pagamento, se houver — relevante para Compra e Venda.",
        },
        {
          nome: "Fornecedor / Cliente / Fazenda Destino / Pasto Destino",
          obrigatorio: true,
          explicacao: "Campo específico do tipo escolhido: Fornecedor em Compras, Cliente em Vendas, Fazenda Destino em transferências entre fazendas, Pasto Destino em transferências entre pastos.",
        },
      ],
    },
  ],
  dicas: [
    "Cadastrar, editar ou excluir uma movimentação exige conexão com a internet — apenas a consulta funciona offline.",
    "Uma venda não permite selecionar animais que já constam como vendidos em outra movimentação.",
  ],
};

export default function GestaoMovimentacoes() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [carregandoLista, setCarregandoLista] = useState(true);
  const [movimentacoes, setMovimentacoes] = useState([]);
  const [movimentacoesFiltradas, setMovimentacoesFiltradas] = useState([]);
  const [editando, setEditando] = useState(false);
  const [editandoId, setEditandoId] = useState(null);
  const [mostrarFormulario, setMostrarFormulario] = useState(false);

  // Dados para os selects
  const [fazendas, setFazendas] = useState([]);
  const [pastos, setPastos] = useState([]);
  const [fornecedores, setFornecedores] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [allAnimals, setAllAnimals] = useState([]);
  const [mostrarModalFornecedor, setMostrarModalFornecedor] = useState(false);
  const [mostrarModalFazenda, setMostrarModalFazenda] = useState(false);
  const [mostrarModalFazendaDestino, setMostrarModalFazendaDestino] = useState(false);
  const [mostrarModalCliente, setMostrarModalCliente] = useState(false);
  const [mostrarModalPasto, setMostrarModalPasto] = useState(false);
  const [mostrarModalAnimal, setMostrarModalAnimal] = useState(false);

  const [filtros, setFiltros] = useState({
    busca: "",
    tipo: "",
    data_inicio: "",
    data_fim: "",
  });

  const [formData, setFormData] = useState({
    fazenda: "",
    tipo_movimentacao: "",
    numero_documento: "",
    data_emissao: "",
    animais: [],
    descricao: "",
    valor_total: "",
    fornecedor: "",
    cliente: "",
    preco_unitario: "",
    data_vencimento: "",
    fazenda_destino: "",
    pasto_destino: "",
  });

  const [selectedAnimals, setSelectedAnimals] = useState([]);
  const [modoSelecao, setModoSelecao] = useState("individual");
  const [loteSelecionado, setLoteSelecionado] = useState("");
  const [filtroAnimal, setFiltroAnimal] = useState("");

  const isFinanceiro = ["C", "V"].includes(formData.tipo_movimentacao);

  const lotesUnicos = [
    ...new Set(allAnimals.map((a) => a.lote_atual?.id).filter(Boolean)),
  ];

  // Carregar dados iniciais
  const carregarFazendas = useCallback(async () => {
    try {
      const response = await repositorioFazenda.listar();
      const dados = response.data.results || response.data;
      setFazendas(Array.isArray(dados) ? dados : []);
    } catch (error) {
      console.error("Erro ao carregar fazendas:", error);
      setFazendas([]);
    }
  }, []);

  const carregarPastos = useCallback(async () => {
    try {
      const response = await repositorioPasto.listar();
      const dados = response.data.results || response.data;
      setPastos(Array.isArray(dados) ? dados : []);
    } catch (error) {
      console.error("Erro ao carregar pastos:", error);
      setPastos([]);
    }
  }, []);

  const carregarFornecedores = useCallback(async () => {
    try {
      const response = await repositorioFornecedor.listar();
      const dados = response.data.results || response.data;
      setFornecedores(Array.isArray(dados) ? dados : []);
    } catch (error) {
      console.error("Erro ao carregar fornecedores:", error);
      setFornecedores([]);
    }
  }, []);

  const carregarClientes = useCallback(async () => {
    try {
      const response = await repositorioCliente.listar();
      const dados = response.data.results || response.data;
      setClientes(Array.isArray(dados) ? dados : []);
    } catch (error) {
      console.error("Erro ao carregar clientes:", error);
      setClientes([]);
    }
  }, []);

  // Carregar animais por fazenda
  const carregarAnimais = useCallback(async (fazendaId) => {
    if (!fazendaId) {
      setAllAnimals([]);
      return;
    }

    try {
      const response = await repositorioAnimal.listar(`?fazenda=${fazendaId}`);
      const dados = response.data.results || response.data;
      setAllAnimals(Array.isArray(dados) ? dados : []);
    } catch (error) {
      console.error("Erro ao carregar animais:", error);
      setAllAnimals([]);
    }
  }, []);

  // Carregar movimentações
  const carregarMovimentacoes = useCallback(async () => {
    try {
      setCarregandoLista(true);
      const response = await repositorioMovimentacaoPecuaria.listar();
      const dados = response.data.results || response.data;
      setMovimentacoes(Array.isArray(dados) ? dados : []);
      setMovimentacoesFiltradas(Array.isArray(dados) ? dados : []);
    } catch (error) {
      console.error("Erro ao carregar movimentações:", error);
      alert("Erro ao carregar lista de movimentações");
    } finally {
      setCarregandoLista(false);
    }
  }, []);

  useEffect(() => {
    carregarMovimentacoes();
    carregarFazendas();
    carregarPastos();
    carregarFornecedores();
    carregarClientes();
  }, [
    carregarMovimentacoes,
    carregarFazendas,
    carregarPastos,
    carregarFornecedores,
    carregarClientes,
  ]);

  useEffect(() => {
    if (formData.fazenda) {
      carregarAnimais(formData.fazenda);
    } else {
      setAllAnimals([]);
      setSelectedAnimals([]);
    }
  }, [formData.fazenda, carregarAnimais]);

  // Calcular valor total automaticamente
  useEffect(() => {
    const quantidade = selectedAnimals.length;
    const preco = parseFloat(formData.preco_unitario || 0);
    const total = quantidade * preco;

    setFormData((prev) => ({
      ...prev,
      valor_total: total.toFixed(2),
    }));
  }, [selectedAnimals, formData.preco_unitario]);

  // Limpar campos financeiros quando não for financeiro
  useEffect(() => {
    if (!isFinanceiro) {
      setFormData((prev) => ({
        ...prev,
        preco_unitario: "",
        valor_total: "",
      }));
    }
  }, [formData.tipo_movimentacao, isFinanceiro]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (name === "fazenda") {
      setSelectedAnimals([]);
    }
  };

  const validarFormulario = () => {
    if (!formData.fazenda) {
      alert("Selecione uma fazenda.");
      return false;
    }
    if (!formData.tipo_movimentacao) {
      alert("Selecione o tipo de movimentação.");
      return false;
    }
    if (!formData.numero_documento.trim()) {
      alert("Informe o número do documento.");
      return false;
    }
    if (!formData.data_emissao) {
      alert("Informe a data de emissão.");
      return false;
    }
    if (selectedAnimals.length === 0) {
      alert("Selecione pelo menos um animal.");
      return false;
    }

    const animaisSelecionados = allAnimals.filter((a) =>
      selectedAnimals.includes(a.id),
    );

    if (formData.tipo_movimentacao === "C" && !formData.fornecedor) {
      alert("Selecione o fornecedor para a compra.");
      return false;
    }

    if (formData.tipo_movimentacao === "V" && !formData.cliente) {
      alert("Selecione o cliente para a venda.");
      return false;
    }

    if (formData.tipo_movimentacao === "T" && !formData.fazenda_destino) {
      alert("Selecione a fazenda de destino para a transferência.");
      return false;
    }

    if (formData.tipo_movimentacao === "P" && !formData.pasto_destino) {
      alert("Selecione o pasto de destino.");
      return false;
    }

    if (
      isFinanceiro &&
      (!formData.preco_unitario || parseFloat(formData.preco_unitario) <= 0)
    ) {
      alert("Informe um preço unitário válido.");
      return false;
    }

    const fazendaDiferente = animaisSelecionados.some(
      (a) => String(a.fazenda) !== String(formData.fazenda),
    );
    if (fazendaDiferente) {
      alert("Todos os animais devem pertencer à fazenda selecionada.");
      return false;
    }

    if (formData.tipo_movimentacao === "V") {
      const jaVendidos = animaisSelecionados.some((a) => a.status === "V");
      if (jaVendidos) {
        alert("Há animais já vendidos na seleção.");
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validarFormulario()) return;

    setLoading(true);

    const payload = {
      ...formData,
      animais: selectedAnimals,
    };

    try {
      if (editando) {
        await api.put(`/pecuaria/movimentacoes/${editandoId}/`, payload);
        alert("Movimentação atualizada com sucesso!");
        handleCancel();
        carregarMovimentacoes();
      } else {
        await api.post("/pecuaria/movimentacoes/", payload);
        alert("Movimentação cadastrada com sucesso!");
        handleCancel();
        carregarMovimentacoes();
        carregarAnimais(formData.fazenda);
      }
    } catch (error) {
      console.error("Erro:", error);
      const data = error.response?.data;
      if (data && typeof data === 'object' && !Array.isArray(data)) {
        const erros = Object.values(data).flat();
        alert(erros.join("\n"));
      } else if (Array.isArray(data)) {
        alert(data.join("\n"));
      } else {
        alert("Erro ao salvar. Verifique os dados e tente novamente.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEditar = (movimentacao) => {
    setFormData({
      fazenda: movimentacao.fazenda?.id || movimentacao.fazenda || "",
      tipo_movimentacao: movimentacao.tipo_movimentacao || "",
      numero_documento: movimentacao.numero_documento || "",
      data_emissao: movimentacao.data_emissao || "",
      descricao: movimentacao.descricao || "",
      valor_total: movimentacao.valor_total || "",
      fornecedor: movimentacao.fornecedor?.id || movimentacao.fornecedor || "",
      cliente: movimentacao.cliente?.id || movimentacao.cliente || "",
      preco_unitario: movimentacao.preco_unitario || "",
      data_vencimento: movimentacao.data_vencimento || "",
      fazenda_destino:
        movimentacao.fazenda_destino?.id || movimentacao.fazenda_destino || "",
      pasto_destino: movimentacao.pasto_destino?.id || movimentacao.pasto_destino || "",
    });
    setSelectedAnimals(movimentacao.animais?.map((a) => a.id || a) || []);
    setEditando(true);
    setEditandoId(movimentacao.id);
    setMostrarFormulario(true);

    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }, 100);
  };

  const handleNovoMovimentacao = () => {
    handleCancel();
    setMostrarFormulario(true);
  };

  const handleCancel = () => {
    setFormData({
      fazenda: "",
      tipo_movimentacao: "",
      numero_documento: "",
      data_emissao: "",
      descricao: "",
      valor_total: "",
      fornecedor: "",
      cliente: "",
      preco_unitario: "",
      data_vencimento: "",
      fazenda_destino: "",
      pasto_destino: "",
    });
    setSelectedAnimals([]);
    setEditando(false);
    setEditandoId(null);
    setMostrarFormulario(false);
    setLoteSelecionado("");
    setModoSelecao("individual");
  };

  const handleExcluir = async (id) => {
    if (
      !window.confirm(
        "Tem certeza que deseja excluir esta movimentação? Esta ação não pode ser desfeita.",
      )
    ) {
      return;
    }

    try {
      await api.delete(`/pecuaria/movimentacoes/${id}/`);
      alert("Movimentação excluída com sucesso!");
      carregarMovimentacoes();

      if (editandoId === id) {
        handleCancel();
      }
    } catch (error) {
      console.error("Erro ao excluir:", error);
      alert("Erro ao excluir movimentação.");
    }
  };

  // Aplicar filtros
  const aplicarFiltros = () => {
    let filtrados = Array.isArray(movimentacoes) ? [...movimentacoes] : [];

    if (filtros.busca) {
      const buscaLower = filtros.busca.toLowerCase();
      filtrados = filtrados.filter((m) =>
        m.numero_documento?.toLowerCase().includes(buscaLower),
      );
    }

    if (filtros.tipo) {
      filtrados = filtrados.filter((m) => m.tipo_movimentacao === filtros.tipo);
    }

    if (filtros.data_inicio) {
      filtrados = filtrados.filter(
        (m) => m.data_emissao >= filtros.data_inicio,
      );
    }

    if (filtros.data_fim) {
      filtrados = filtrados.filter((m) => m.data_emissao <= filtros.data_fim);
    }

    setMovimentacoesFiltradas(filtrados);
  };

  const limparFiltros = () => {
    setFiltros({ busca: "", tipo: "", data_inicio: "", data_fim: "" });
    setMovimentacoesFiltradas(movimentacoes);
  };

  const getTipoLabel = (tipo) => {
    const tipos = {
      C: "Compra",
      V: "Venda",
      T: "Transferência",
      P: "Entre pastos",
    };
    return tipos[tipo] || tipo;
  };

  const getTipoColor = (tipo) => {
    const cores = {
      C: "bg-blue-100 text-blue-800",
      V: "bg-green-100 text-green-800",
      T: "bg-purple-100 text-purple-800",
      P: "bg-yellow-100 text-yellow-800",
    };
    return cores[tipo] || "bg-gray-100 text-gray-800";
  };

  const formatarData = (data) => {
    if (!data) return "—";
    return new Date(data).toLocaleDateString("pt-BR", { timeZone: "UTC" });
  };

  const formatarMoeda = (valor) => {
    if (!valor) return "—";
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(valor);
  };

  const getAnimalDisplay = (animal) => {
    const identificacao =
      animal.sisbov || animal.numero_identificacao || `ID: ${animal.id}`;
    const nome = animal.nome || "Sem nome";
    return `${identificacao} - ${nome}`;
  };

  const animaisFiltrados = allAnimals.filter(
    (animal) =>
      animal.nome?.toLowerCase().includes(filtroAnimal.toLowerCase()) ||
      animal.sisbov?.toLowerCase().includes(filtroAnimal.toLowerCase()),
  );

  return (
    <Layout>
      <div className="p-6 font-medium max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center gap-2">
            <h2 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
              Gestão de Movimentações <MdReceipt size={38} />
            </h2>
            <AjudaTela {...AJUDA} />
          </div>
          {!mostrarFormulario && (
            <button
              onClick={handleNovoMovimentacao}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors flex items-center gap-2 shadow-md"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
              Nova Movimentação
            </button>
          )}
        </div>

        {/* Formulário de Cadastro/Edição */}
        {mostrarFormulario && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-8 border border-gray-200">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-gray-800">
                {editando ? "Editar Movimentação" : "Nova Movimentação"}
              </h3>
              <button
                onClick={handleCancel}
                className="text-gray-500 hover:text-gray-700"
                title="Fechar formulário"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="space-y-6">
                {/* Seção 1: Informações Básicas */}
                <div className="border-b pb-4">
                  <h4 className="text-lg font-semibold text-gray-700 mb-4">
                    Dados da Movimentação
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className="flex flex-col">
                      <label className="text-sm font-medium text-gray-700 mb-1">
                        Fazenda *
                      </label>
                      <div className="flex items-center gap-2">
                        <select
                          name="fazenda"
                          value={formData.fazenda}
                          onChange={handleChange}
                          className="flex-1 border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                          required
                        >
                          <option value="">Selecione uma fazenda</option>
                          {fazendas.map((f) => (
                            <option key={f.id} value={f.id}>
                              {f.nome}
                            </option>
                          ))}
                        </select>
                        <button
                        type="button"
                        onClick={() => setMostrarModalFazenda(true)}
                        title="Cadastrar nova fazenda"
                        className="p-2 hover:bg-gray-200 rounded"
                      >
                        <LuCopyPlus size={26} className="text-green-600 hover:text-green-800" />
                      </button>
                      </div>
                    </div>

                    <div className="flex flex-col">
                      <label className="text-sm font-medium text-gray-700 mb-1">
                        Tipo de Movimentação *
                      </label>
                      <select
                        name="tipo_movimentacao"
                        value={formData.tipo_movimentacao}
                        onChange={handleChange}
                        className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                        required
                      >
                        <option value="">Selecione...</option>
                        <option value="C">Compra</option>
                        <option value="V">Venda</option>
                        <option value="T">Transferência entre fazendas</option>
                        <option value="P">Transferência entre pastos</option>
                      </select>
                    </div>

                    <div className="flex flex-col">
                      <label className="text-sm font-medium text-gray-700 mb-1">
                        Número do Documento *
                      </label>
                      <input
                        type="text"
                        name="numero_documento"
                        value={formData.numero_documento}
                        onChange={handleChange}
                        className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                        placeholder="NF-12345, CTE-6789, etc."
                        required
                      />
                    </div>

                    <div className="flex flex-col">
                      <label className="text-sm font-medium text-gray-700 mb-1">
                        Data de Emissão *
                      </label>
                      <input
                        type="date"
                        name="data_emissao"
                        value={formData.data_emissao}
                        onChange={handleChange}
                        className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                        required
                      />
                    </div>

                    <div className="flex flex-col">
                      <label className="text-sm font-medium text-gray-700 mb-1">
                        Data de Vencimento
                      </label>
                      <input
                        type="date"
                        name="data_vencimento"
                        value={formData.data_vencimento}
                        onChange={handleChange}
                        className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                      />
                    </div>

                    {formData.tipo_movimentacao === "C" && (
                      <div className="flex flex-col">
                        <label className="text-sm font-medium text-gray-700 mb-1">
                          Fornecedor *
                        </label>
                        <div className="flex items-center gap-2">
                          <select
                            name="fornecedor"
                            value={formData.fornecedor}
                            onChange={handleChange}
                            className="flex-1 border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                            required
                          >
                            <option value="">Selecione um fornecedor</option>
                            {fornecedores.map((f) => (
                              <option key={f.id} value={f.id}>
                                {nomePessoa(f) || "—"}
                              </option>
                            ))}
                          </select>
                          <button
                            type="button"
                            onClick={() => setMostrarModalFornecedor(true)}
                            title="Cadastrar novo fornecedor"
                            className="p-2 hover:bg-gray-200 rounded"
                          >
                            <LuCopyPlus size={26} className="text-green-600 hover:text-green-800" />
                          </button>
                        </div>
                      </div>
                    )}

                    {formData.tipo_movimentacao === "V" && (
                      <div className="flex flex-col">
                        <label className="text-sm font-medium text-gray-700 mb-1">
                          Cliente *
                        </label>
                        <div className="flex items-center gap-2">
                          <select
                            name="cliente"
                            value={formData.cliente}
                            onChange={handleChange}
                            className="flex-1 border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                            required
                          >
                            <option value="">Selecione um cliente</option>
                            {clientes.map((c) => (
                              <option key={c.id} value={c.id}>
                                {nomePessoa(c) || "—"}
                              </option>
                            ))}
                          </select>
                          <button
                            type="button"
                            onClick={() => setMostrarModalCliente(true)}
                            title="Cadastrar novo cliente"
                            className="p-2 hover:bg-gray-200 rounded"
                          >
                            <LuCopyPlus size={26} className="text-green-600 hover:text-green-800" />
                          </button>
                        </div>
                      </div>
                    )}

                    {formData.tipo_movimentacao === "T" && (
                      <div className="flex flex-col">
                        <label className="text-sm font-medium text-gray-700 mb-1">
                          Fazenda Destino *
                        </label>
                        <div className="flex items-center gap-2">
                          <select
                            name="fazenda_destino"
                            value={formData.fazenda_destino}
                            onChange={handleChange}
                            className="flex-1 border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                            required
                          >
                            <option value="">
                              Selecione a fazenda destino
                            </option>
                            {fazendas
                              .filter((f) => f.id !== formData.fazenda)
                              .map((f) => (
                                <option key={f.id} value={f.id}>
                                  {f.nome}
                                </option>
                              ))}
                          </select>
                          <button
                            type="button"
                            onClick={() => setMostrarModalFazendaDestino(true)}
                            title="Cadastrar nova fazenda"
                            className="p-2 hover:bg-gray-200 rounded"
                          >
                            <LuCopyPlus size={26} className="text-green-600 hover:text-green-800" />
                          </button>
                        </div>
                      </div>
                    )}

                    {formData.tipo_movimentacao === "P" && (
                      <div className="flex flex-col">
                        <label className="text-sm font-medium text-gray-700 mb-1">
                          Pasto Destino *
                        </label>
                        <div className="flex items-center gap-2">
                          <select
                            name="pasto_destino"
                            value={formData.pasto_destino}
                            onChange={handleChange}
                            className="flex-1 border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                            required
                          >
                            <option value="">Selecione o pasto destino</option>
                            {pastos
                              .filter((p) => p.fazenda === formData.fazenda)
                              .map((p) => (
                                <option key={p.id} value={p.id}>
                                  {p.nome}
                                </option>
                              ))}
                          </select>
                          <button
                            type="button"
                            onClick={() => setMostrarModalPasto(true)}
                            title="Cadastrar novo pasto"
                            className="p-2 hover:bg-gray-200 rounded"
                          >
                            <LuCopyPlus size={26} className="text-green-600 hover:text-green-800" />
                          </button>
                        </div>
                      </div>
                    )}

                    {isFinanceiro && (
                      <>
                        <div className="flex flex-col">
                          <label className="text-sm font-medium text-gray-700 mb-1">
                            Preço Unitário *
                          </label>
                          <div className="flex items-center border border-green-500 rounded">
                            <span className="px-3 text-gray-700 bg-gray-100 rounded-l">
                              R$
                            </span>
                            <input
                              type="number"
                              name="preco_unitario"
                              value={formData.preco_unitario}
                              onChange={handleChange}
                              className="flex-1 p-2 rounded-r focus:ring-2 focus:ring-green-300"
                              step="0.01"
                              min="0"
                              placeholder="0,00"
                              required
                            />
                          </div>
                        </div>

                        <div className="flex flex-col">
                          <label className="text-sm font-medium text-gray-700 mb-1">
                            Valor Total
                          </label>
                          <div className="flex items-center border border-gray-300 rounded bg-gray-100">
                            <span className="px-3 text-gray-700 bg-gray-100 rounded-l">
                              R$
                            </span>
                            <input
                              type="text"
                              value={formatarMoeda(formData.valor_total)}
                              readOnly
                              className="flex-1 p-2 rounded-r bg-gray-100 text-gray-700"
                            />
                          </div>
                          <span className="text-xs text-gray-500 mt-1">
                            {selectedAnimals.length} animal(is) × R${" "}
                            {formData.preco_unitario || 0}
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Seção 2: Seleção de Animais */}
                <div className="border-b pb-4">
                  <h4 className="text-lg font-semibold text-gray-700 mb-4">
                    Seleção de Animais
                  </h4>

                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <div className="lg:col-span-1 space-y-4">
                      <div className="flex flex-col">
                        <label className="text-sm font-medium text-gray-700 mb-1">
                          Modo de seleção
                        </label>
                        <select
                          value={modoSelecao}
                          onChange={(e) => setModoSelecao(e.target.value)}
                          className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                        >
                          <option value="individual">
                            Selecionar individualmente
                          </option>
                          <option value="lote">Selecionar por lote</option>
                        </select>
                      </div>

                      {modoSelecao === "lote" && lotesUnicos.length > 0 && (
                        <div className="flex flex-col">
                          <label className="text-sm font-medium text-gray-700 mb-1">
                            Selecionar Lote
                          </label>
                          <select
                            value={loteSelecionado}
                            onChange={(e) => {
                              const loteId = e.target.value;
                              setLoteSelecionado(loteId);
                              const animaisDoLote = allAnimals.filter(
                                (a) => String(a.lote_atual?.id) === loteId,
                              );
                              setSelectedAnimals(
                                animaisDoLote.map((a) => a.id),
                              );
                            }}
                            className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                          >
                            <option value="">Selecione o lote...</option>
                            {lotesUnicos.map((id) => {
                              const lote = allAnimals.find(
                                (a) => a.lote_atual?.id === id,
                              )?.lote_atual;
                              return (
                                <option key={id} value={id}>
                                  {lote?.nome || `Lote ${id}`}
                                </option>
                              );
                            })}
                          </select>
                        </div>
                      )}

                      <div className="flex gap-3">
                        <button
                          type="button"
                          onClick={() =>
                            setSelectedAnimals(
                              animaisFiltrados.map((a) => a.id),
                            )
                          }
                          className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded transition-colors text-sm"
                        >
                          Selecionar todos
                        </button>
                        <button
                          type="button"
                          onClick={() => setSelectedAnimals([])}
                          className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded transition-colors text-sm"
                        >
                          Limpar seleção
                        </button>
                      </div>
                    </div>

                    <div className="lg:col-span-2">
                      <div className="flex items-center gap-2 mb-3">
                        <input
                          type="text"
                          placeholder="Pesquisar animal por nome, SISBOV ou brinco..."
                          value={filtroAnimal}
                          onChange={(e) => setFiltroAnimal(e.target.value)}
                          className="flex-1 border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                        />
                        <button
                          type="button"
                          onClick={() => setMostrarModalAnimal(true)}
                          title="Cadastrar novo animal"
                          className="p-2 hover:bg-gray-200 rounded"
                        >
                          <LuCopyPlus size={26} className="text-green-600 hover:text-green-800" />
                        </button>
                      </div>

                      <div className="border border-green-500 rounded-lg p-3 max-h-80 overflow-y-auto">
                        {animaisFiltrados.length === 0 ? (
                          <p className="text-center text-gray-500 py-4">
                            {formData.fazenda
                              ? "Nenhum animal encontrado"
                              : "Selecione uma fazenda primeiro"}
                          </p>
                        ) : (
                          animaisFiltrados.map((animal) => (
                            <label
                              key={animal.id}
                              className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded cursor-pointer transition-colors"
                            >
                              <input
                                type="checkbox"
                                checked={selectedAnimals.includes(animal.id)}
                                onChange={() => {
                                  if (selectedAnimals.includes(animal.id)) {
                                    setSelectedAnimals((prev) =>
                                      prev.filter((id) => id !== animal.id),
                                    );
                                  } else {
                                    setSelectedAnimals((prev) => [
                                      ...prev,
                                      animal.id,
                                    ]);
                                  }
                                }}
                                className="w-4 h-4 text-green-600"
                              />
                              <span className="flex-1">
                                <span className="font-medium">
                                  {getAnimalDisplay(animal)}
                                </span>
                                <span className="text-sm text-gray-500 ml-2">
                                  {animal.lote_atual?.nome
                                    ? `Lote: ${animal.lote_atual.nome}`
                                    : "Sem lote"}
                                </span>
                              </span>
                              <span
                                className={`text-xs px-2 py-1 rounded-full ${
                                  animal.status === "A"
                                    ? "bg-green-100 text-green-800"
                                    : animal.status === "V"
                                      ? "bg-blue-100 text-blue-800"
                                      : "bg-gray-100 text-gray-800"
                                }`}
                              >
                                {animal.status === "A"
                                  ? "Ativo"
                                  : animal.status === "V"
                                    ? "Vendido"
                                    : "Outro"}
                              </span>
                            </label>
                          ))
                        )}
                      </div>

                      <div className="mt-3 text-sm text-gray-600">
                        {selectedAnimals.length} animal(is) selecionado(s)
                      </div>
                    </div>
                  </div>
                </div>

                {/* Seção 3: Observações */}
                <div>
                  <h4 className="text-lg font-semibold text-gray-700 mb-4">
                    Observações
                  </h4>
                  <div className="flex flex-col">
                    <textarea
                      name="descricao"
                      value={formData.descricao}
                      onChange={handleChange}
                      className="w-full border border-green-500 p-3 rounded focus:ring-2 focus:ring-green-300"
                      rows="4"
                      placeholder="Observações ou detalhes da movimentação..."
                    />
                  </div>
                </div>

                {/* Botões do formulário */}
                <div className="flex gap-4 pt-4">
                  <button
                    type="submit"
                    disabled={loading}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors disabled:bg-blue-300"
                  >
                    {loading
                      ? "Salvando..."
                      : editando
                        ? "Atualizar"
                        : "Cadastrar"}
                  </button>
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg transition-colors"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </form>
          </div>
        )}

        {/* Seção de Filtros da Consulta - Sempre visível quando não está editando */}
        {!mostrarFormulario && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-8 border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-700 mb-4 flex items-center gap-2">
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
                />
              </svg>
              Filtros de Busca
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 mb-1">
                  Buscar por Documento
                </label>
                <input
                  type="text"
                  placeholder="Número do documento..."
                  value={filtros.busca}
                  onChange={(e) =>
                    setFiltros({ ...filtros, busca: e.target.value })
                  }
                  className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                />
              </div>

              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 mb-1">
                  Tipo
                </label>
                <select
                  value={filtros.tipo}
                  onChange={(e) =>
                    setFiltros({ ...filtros, tipo: e.target.value })
                  }
                  className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                >
                  <option value="">Todos</option>
                  <option value="C">Compra</option>
                  <option value="V">Venda</option>
                  <option value="T">Transferência</option>
                  <option value="P">Entre pastos</option>
                </select>
              </div>

              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 mb-1">
                  Data Início
                </label>
                <input
                  type="date"
                  value={filtros.data_inicio}
                  onChange={(e) =>
                    setFiltros({ ...filtros, data_inicio: e.target.value })
                  }
                  className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                />
              </div>

              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 mb-1">
                  Data Fim
                </label>
                <input
                  type="date"
                  value={filtros.data_fim}
                  onChange={(e) =>
                    setFiltros({ ...filtros, data_fim: e.target.value })
                  }
                  className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-4">
              <button
                onClick={aplicarFiltros}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded transition-colors"
              >
                Aplicar Filtros
              </button>
              <button
                onClick={limparFiltros}
                className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded transition-colors"
              >
                Limpar Filtros
              </button>
            </div>
          </div>
        )}

        {/* Tabela de Movimentações - Sempre visível quando não está editando */}
        {!mostrarFormulario && (
          <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
            <div className="p-4 border-b border-gray-200 bg-gray-50">
              <p className="text-gray-600">
                {movimentacoesFiltradas.length} movimentação(ões) encontrada(s)
              </p>
            </div>

            {carregandoLista ? (
              <div className="p-8 text-center text-gray-500">Carregando...</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="p-3 text-left text-sm font-semibold text-gray-700">
                        Documento
                      </th>
                      <th className="p-3 text-left text-sm font-semibold text-gray-700">
                        Tipo
                      </th>
                      <th className="p-3 text-left text-sm font-semibold text-gray-700">
                        Data Emissão
                      </th>
                      <th className="p-3 text-left text-sm font-semibold text-gray-700">
                        Vencimento
                      </th>
                      <th className="p-3 text-left text-sm font-semibold text-gray-700">
                        Animais
                      </th>
                      <th className="p-3 text-left text-sm font-semibold text-gray-700">
                        Valor Total
                      </th>
                      <th className="p-3 text-left text-sm font-semibold text-gray-700">
                        Ações
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {movimentacoesFiltradas.map((mov) => (
                      <tr
                        key={mov.id}
                        className="border-t border-gray-200 hover:bg-gray-50 transition-colors"
                      >
                        <td className="p-3 text-sm font-mono">
                          {mov.numero_documento}
                        </td>
                        <td className="p-3">
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-medium ${getTipoColor(mov.tipo_movimentacao)}`}
                          >
                            {getTipoLabel(mov.tipo_movimentacao)}
                          </span>
                        </td>
                        <td className="p-3 text-sm">
                          {formatarData(mov.data_emissao)}
                        </td>
                        <td className="p-3 text-sm">
                          {formatarData(mov.data_vencimento)}
                        </td>
                        <td className="p-3 text-sm">
                          {mov.animais?.length || 0} animais
                        </td>
                        <td className="p-3 text-sm font-medium">
                          {formatarMoeda(mov.valor_total)}
                        </td>
                        <td className="p-3">
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleEditar(mov)}
                              className="bg-yellow-500 hover:bg-yellow-600 text-white px-3 py-1 rounded text-sm transition-colors"
                              title="Editar"
                            >
                              Editar
                            </button>
                            <button
                              onClick={() => handleExcluir(mov.id)}
                              className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm transition-colors"
                              title="Excluir"
                            >
                              Excluir
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}

                    {movimentacoesFiltradas.length === 0 && (
                      <tr>
                        <td
                          colSpan="7"
                          className="p-8 text-center text-gray-500"
                        >
                          {filtros.busca ||
                          filtros.tipo ||
                          filtros.data_inicio ||
                          filtros.data_fim
                            ? "Nenhuma movimentação encontrada com os filtros selecionados."
                            : "Nenhuma movimentação cadastrada."}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      <ModalCadastroFornecedor
        isOpen={mostrarModalFornecedor}
        onClose={() => setMostrarModalFornecedor(false)}
        onSuccess={async (novo) => {
          await carregarFornecedores();
          setFormData((prev) => ({ ...prev, fornecedor: novo.id }));
        }}
      />
      <ModalCadastroFazenda
        isOpen={mostrarModalFazenda}
        onClose={() => setMostrarModalFazenda(false)}
        onSuccess={async (nova) => {
          await carregarFazendas();
          setFormData((prev) => ({ ...prev, fazenda: nova.id }));
        }}
      />
      <ModalCadastroFazenda
        isOpen={mostrarModalFazendaDestino}
        onClose={() => setMostrarModalFazendaDestino(false)}
        onSuccess={async (nova) => {
          await carregarFazendas();
          setFormData((prev) => ({ ...prev, fazenda_destino: nova.id }));
        }}
      />
      <ModalCadastroCliente
        isOpen={mostrarModalCliente}
        onClose={() => setMostrarModalCliente(false)}
        onSuccess={async (novo) => {
          await carregarClientes();
          setFormData((prev) => ({ ...prev, cliente: novo.id }));
        }}
      />
      <ModalCadastroPasto
        isOpen={mostrarModalPasto}
        onClose={() => setMostrarModalPasto(false)}
        fazendaSelecionadaId={formData.fazenda}
        onSuccess={async (novo) => {
          await carregarPastos();
          setFormData((prev) => ({ ...prev, pasto_destino: novo.id }));
        }}
      />
      <ModalCadastroAnimal
        isOpen={mostrarModalAnimal}
        onClose={() => setMostrarModalAnimal(false)}
        fazendaSelecionadaId={formData.fazenda}
        onSuccess={async (novo) => {
          await carregarAnimais(formData.fazenda);
          setSelectedAnimals((prev) => [...prev, novo.id]);
        }}
      />
    </Layout>
  );
}
